import { DvToolbarAction } from '@devinco/ui/grid';
import { Injectable, inject } from '@angular/core';
import { ColumnType } from '@features/vt-grid/grid-options/column-types';
import { GridContext } from '@features/vt-grid/interfaces/grid-context';
import { ToolbarActionsService } from '@features/vt-grid/services/toolbar-actions.service';
import { Entity } from '@shared/constants/entity';
import { POIDto } from '@pages/poi-page/interfaces';
import { ColDef, GridApi, ValueFormatterParams } from 'ag-grid-community';
import { vtPresetActions } from '@features/vt-grid/presets/preset-actions';
import { UserMetadataService } from '@core/services/user-metadata.service';

@Injectable({
  providedIn: 'root',
})
export class PoiGridService {
  private toolbarActionService = inject(ToolbarActionsService);
  private metaDataService = inject(UserMetadataService);

  gridApi: GridApi;
  gridContext: GridContext;

  colDefs: ColDef<POIDto>[] = [
    {
      headerName: 'Grid.Id',
      field: 'POIID',
      hide: true,
      type: [ColumnType.Id],
    },
    {
      field: 'Active',
      headerName: 'POI.Active',
      type: [ColumnType.Checkbox],
    },
    {
      field: 'Description',
      headerName: 'POI.Description',
      type: [ColumnType.Text],
    },
    {
      field: 'POIType_TranslationKey',
      headerName: 'POI.POITypeName',
      type: [
        this.metaDataService.hasSkiDomain()
          ? ColumnType.TranslatedText
          : ColumnType.Invisible,
      ],
    },
    {
      field: 'GPSLat',
      headerName: 'POI.Lat',
      type: [ColumnType.Number],
      enableRowGroup: false,
      cellStyle: { 'text-align': 'right' },
      valueFormatter: (params: ValueFormatterParams) =>
        params.value ? params.value.toFixed(5) : params.value,
    },
    {
      field: 'GPSLon',
      headerName: 'POI.Lon',
      type: [ColumnType.Number],
      enableRowGroup: false,
      cellStyle: { 'text-align': 'right' },
      valueFormatter: (params: ValueFormatterParams) =>
        params.value ? params.value.toFixed(5) : params.value,
    },
    {
      field: 'Address',
      headerName: 'POI.Address',
      type: [ColumnType.Text],
    },
    {
      field: 'PostalNumber',
      headerName: 'POI.PostalNumber',
      type: [ColumnType.NumberText],
    },
    {
      field: 'PostalPlace',
      headerName: 'POI.PostalPlace',
      type: [ColumnType.Text],
    },
    { field: 'Country', headerName: 'POI.Country', type: [ColumnType.Text] },
    {
      field: 'AcceptanceRadius',
      headerName: 'POI.AcceptanceRadius',
      type: [ColumnType.NumberText],
    },
    {
      field: 'Purpose',
      headerName: 'POI.Purpose',
      type: [ColumnType.Text],
      flex: 1,
    },
    {
      field: 'Personal',
      headerName: 'POI.Personal',
      type: [ColumnType.Checkbox],
    },
    { field: 'IsPublic', type: ColumnType.Invisible },
    { field: 'IconKey', type: ColumnType.Invisible },
    { field: 'IconColor', type: ColumnType.Invisible },
    { field: 'IconSize', type: ColumnType.Invisible },
    { field: 'SeverityID', type: ColumnType.Invisible },
    { field: 'DomainID', type: ColumnType.Invisible },
    { field: 'DomainName', type: ColumnType.Invisible },
    { field: 'Domain_TranslationKey', type: ColumnType.Invisible },
    { field: 'SeverityName', type: ColumnType.Invisible },
    { field: 'Severity_TranslationKey', type: ColumnType.Invisible },
  ];

  getGridActions(onEditClick: () => void): DvToolbarAction[] {
    const { deleteSelectedRow, serverSideExcelExport } =
      this.toolbarActionService;
    return [
      vtPresetActions.reset,
      vtPresetActions.fit,
      vtPresetActions.reload,
      {
        ...vtPresetActions.excel,
        clickFn: event =>
          serverSideExcelExport(Entity.POI, event.api, event.context),
      },
      {},
      {
        icon: 'delete',
        clickFn: deleteSelectedRow,
        disabled: true,
        tooltipKey: 'Grid.Delete',
      },
      {
        icon: 'edit',
        clickFn: onEditClick,
        disabled: true,
        tooltipKey: 'Grid.Edit',
      },
    ];
  }
}
